import { createApp } from 'vue';
import { convexVue } from 'convex-vue';
import App from './App.vue';
import router from './router';
import { createI18nInstance } from './plugins/i18n';
import './style.css';

const app = createApp(App);

// Initialize i18n with browser language (will be updated once user settings load)
const i18n = createI18nInstance();
app.use(i18n);

// Convex Vue plugin
app.use(convexVue, {
  url: import.meta.env.VITE_CONVEX_URL,
});

app.use(router);

// Global error handler for auth errors - auto-refresh token when expired
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

const JWT_STORAGE_KEY = '__convexAuthJWT';
const REFRESH_TOKEN_STORAGE_KEY = '__convexAuthRefreshToken';
const convexUrl = import.meta.env.VITE_CONVEX_URL ?? '';

const getStorageKey = (key: string): string => {
  if (!convexUrl) return key;
  const escapedNamespace = convexUrl.replace(/[^a-zA-Z0-9]/g, '');
  return `${key}_${escapedNamespace}`;
};

const storageGet = (key: string): string | null => {
  try {
    return localStorage.getItem(getStorageKey(key));
  } catch {
    return null;
  }
};

const storageSet = (key: string, value: string): void => {
  try {
    localStorage.setItem(getStorageKey(key), value);
  } catch {
    // Storage not available
  }
};

const storageRemove = (key: string): void => {
  try {
    localStorage.removeItem(getStorageKey(key));
  } catch {
    // Storage not available
  }
};

const refreshToken = async (): Promise<boolean> => {
  const storedRefreshToken = storageGet(REFRESH_TOKEN_STORAGE_KEY);
  if (!storedRefreshToken) {
    return false;
  }

  try {
    const { ConvexHttpClient } = await import('convex/browser');
    const client = new ConvexHttpClient(convexUrl);
    const result = (await client.action(
      'auth:signIn' as never,
      {
        refreshToken: storedRefreshToken,
      } as never
    )) as { tokens?: { token: string; refreshToken: string } | null };

    if (result.tokens) {
      storageSet(JWT_STORAGE_KEY, result.tokens.token);
      if (result.tokens.refreshToken) {
        storageSet(REFRESH_TOKEN_STORAGE_KEY, result.tokens.refreshToken);
      }
      return true;
    }
  } catch {
    // Refresh failed
  }

  storageRemove(JWT_STORAGE_KEY);
  storageRemove(REFRESH_TOKEN_STORAGE_KEY);
  return false;
};

const handleAuthError = async (): Promise<boolean> => {
  if (isRefreshing) {
    return refreshPromise!;
  }

  isRefreshing = true;
  refreshPromise = refreshToken();

  const success = await refreshPromise;
  isRefreshing = false;
  refreshPromise = null;

  return success;
};

// Global error handler
app.config.errorHandler = (err: unknown) => {
  const errorMessage = (err as Error)?.message || '';

  // Check if it's an auth error
  if (
    errorMessage.includes('Unauthenticated') ||
    errorMessage.includes('Could not verify OIDC token') ||
    (errorMessage.includes('token') && errorMessage.includes('expired'))
  ) {
    handleAuthError().then(success => {
      if (success) {
        window.location.reload();
      } else {
        router.push('/login');
      }
    });
    return;
  }

  // Log other errors
  console.error('[Global Error Handler]', err);
};

// Listen for stale connection events from useConvexQueryWithStaleDetection
window.addEventListener('convex:stale-connection', () => {
  console.log('[Main] Received stale connection event, attempting token refresh...');
  handleAuthError().then(success => {
    if (success) {
      console.log('[Main] Token refreshed successfully, reloading page...');
      window.location.reload();
    } else {
      console.warn('[Main] Token refresh failed, redirecting to login...');
      router.push('/login');
    }
  });
});

app.mount('#app');
